
import { useState } from 'react'
import { Copy, CheckCircle2, BookOpen, Zap, KeyRound, BrainCircuit, Globe, Shield, ChevronDown, ChevronRight, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PLATFORM_DOMAIN, D1_WORKER_URL } from '@/lib/constants'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2000) }}
      className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded"
      aria-label="Copy code"
    >
      {copied ? <CheckCircle2 className="w-3.5 h-3.5 text-success" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  )
}

function CodeBlock({ lang, code }: { lang: string; code: string }) {
  return (
    <div className="rounded-md border border-border overflow-hidden">
      <div className="flex items-center justify-between px-3 py-1.5 bg-muted/80 border-b border-border">
        <span className="text-[10px] text-muted-foreground font-mono">{lang}</span>
        <CopyButton text={code} />
      </div>
      <pre className="p-3 text-[11px] font-mono overflow-x-auto leading-relaxed text-foreground bg-muted/30">
        <code>{code}</code>
      </pre>
    </div>
  )
}

function MethodBadge({ method }: { method: string }) {
  const colors: Record<string, string> = {
    GET: 'border-success/40 text-success bg-success/5',
    POST: 'border-lava/40 text-lava bg-lava/5',
    DELETE: 'border-destructive/40 text-destructive bg-destructive/5',
    PATCH: 'border-warning/40 text-warning bg-warning/5',
  }
  return (
    <Badge variant="outline" className={`text-[10px] font-mono px-2 h-5 flex-shrink-0 ${colors[method] ?? 'border-border text-muted-foreground'}`}>
      {method}
    </Badge>
  )
}

interface EndpointProps {
  method: string
  path: string
  description: string
  auth?: boolean
  children?: React.ReactNode
}

function Endpoint({ method, path, description, auth = true, children }: EndpointProps) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-2.5 w-full px-3 py-2.5 bg-card hover:bg-muted/30 transition-colors text-left"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
        <MethodBadge method={method} />
        <code className="text-xs font-mono text-foreground flex-1">{path}</code>
        {auth && (
          <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-cyan-accent/30 text-cyan-accent bg-cyan-accent/5 hidden sm:flex">
            auth
          </Badge>
        )}
        <span className="text-[11px] text-muted-foreground hidden md:block">{description}</span>
      </button>
      {open && children && (
        <div className="p-4 bg-muted/20 border-t border-border space-y-3">
          {children}
        </div>
      )}
    </div>
  )
}

const BASE = `https://${PLATFORM_DOMAIN}/api`
const GW = `${BASE}/gateway`

export default function DocsPage() {
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-md bg-lava/10">
          <BookOpen className="w-4 h-4 text-lava" />
        </div>
        <div>
          <h2 className="text-base font-bold text-foreground">API Documentation</h2>
          <p className="text-xs text-muted-foreground">
            OpenAI-compatible REST API · Web Crypto signed keys · Cloudflare AI Gateway
          </p>
        </div>
      </div>

      {/* Base URL box */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="p-3 rounded-lg bg-lava/5 border border-lava/20">
          <p className="text-[9px] uppercase tracking-wider text-lava font-semibold mb-1.5">AI Gateway (OpenAI-compatible)</p>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono text-foreground flex-1 break-all">{GW}</code>
            <CopyButton text={GW} />
          </div>
        </div>
        <div className="p-3 rounded-lg bg-cyan-accent/5 border border-cyan-accent/20">
          <p className="text-[9px] uppercase tracking-wider text-cyan-accent font-semibold mb-1.5">D1 REST Worker</p>
          <div className="flex items-center gap-2">
            <code className="text-xs font-mono text-foreground flex-1 break-all">https://{D1_WORKER_URL}</code>
            <CopyButton text={`https://${D1_WORKER_URL}`} />
          </div>
        </div>
      </div>

      <Tabs defaultValue="quickstart">
        <TabsList className="bg-muted border border-border h-8 p-0.5 flex-wrap">
          <TabsTrigger value="quickstart" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <Zap className="w-3 h-3" /> Quickstart
          </TabsTrigger>
          <TabsTrigger value="auth" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <KeyRound className="w-3 h-3" /> Auth
          </TabsTrigger>
          <TabsTrigger value="gateway" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <BrainCircuit className="w-3 h-3" /> AI Gateway
          </TabsTrigger>
          <TabsTrigger value="reference" className="text-xs h-7 px-3 data-[state=active]:bg-card gap-1.5">
            <Globe className="w-3 h-3" /> Reference
          </TabsTrigger>
        </TabsList>

        {/* Quickstart */}
        <TabsContent value="quickstart" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Zap className="w-4 h-4 text-lava" />
                Drop-in OpenAI SDK replacement
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                LavaFlow is fully OpenAI API-compatible. Point any OpenAI SDK at the gateway URL and it works instantly.
              </p>
              <CodeBlock lang="bash — install" code="npm install openai" />
              <CodeBlock lang="typescript" code={`import OpenAI from 'openai'

const lava = new OpenAI({
  apiKey: 'lfk_v1_YOUR_KEY_HERE',
  baseURL: '${GW}',
})

// Streaming chat completion
const stream = await lava.chat.completions.create({
  model: '@cf/meta/llama-3.1-8b-instruct',
  messages: [
    { role: 'system', content: 'You are a helpful assistant.' },
    { role: 'user', content: 'Explain edge computing in one sentence.' },
  ],
  stream: true,
})

for await (const chunk of stream) {
  process.stdout.write(chunk.choices[0]?.delta?.content ?? '')
}
`} />
              <CodeBlock lang="python" code={`from openai import OpenAI

client = OpenAI(
    api_key="lfk_v1_YOUR_KEY_HERE",
    base_url="${GW}",
)

response = client.chat.completions.create(
    model="@cf/meta/llama-3.1-8b-instruct",
    messages=[{"role": "user", "content": "Hello from Python!"}],
    stream=False,
)

print(response.choices[0].message.content)
`} />
              <CodeBlock lang="curl" code={`curl ${GW}/v1/chat/completions \\
  -H "Authorization: Bearer lfk_v1_YOUR_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "model": "@cf/meta/llama-3.1-8b-instruct",
    "messages": [{"role": "user", "content": "Hello!"}],
    "stream": false
  }'`} />
            </CardContent>
          </Card>

          {/* Vercel AI SDK */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Shield className="w-4 h-4 text-cyan-accent" />
                Vercel AI SDK
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <CodeBlock lang="typescript" code={`import { createOpenAI } from '@ai-sdk/openai'
import { streamText } from 'ai'

const lava = createOpenAI({
  apiKey: 'lfk_v1_YOUR_KEY_HERE',
  baseURL: '${GW}',
})

const result = streamText({
  model: lava('@cf/meta/llama-3.1-8b-instruct'),
  prompt: 'Write a haiku about edge computing.',
})

for await (const text of result.textStream) {
  process.stdout.write(text)
}`} />
            </CardContent>
          </Card>

          {/* Local Ollama */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Globe className="w-4 h-4 text-warning" />
                Local Ollama (via AI Gateway)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Route through LavaFlow to your local Ollama instance. Add Ollama as a provider in the AI Gateway tab first.
              </p>
              <CodeBlock lang="typescript" code={`// Add provider in Gateway → Add Provider
// Type: ollama, Endpoint: http://localhost:11434/v1

import OpenAI from 'openai'

const lava = new OpenAI({
  apiKey: 'lfk_v1_YOUR_OLLAMA_KEY',  // key with ollama provider_id
  baseURL: '${GW}',
})

const response = await lava.chat.completions.create({
  model: 'llama3.2',
  messages: [{ role: 'user', content: 'Hello local model!' }],
})
`} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Auth */}
        <TabsContent value="auth" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-lava" />
                API Key Architecture
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-2">
                {[
                  { step: '1', title: 'Key Generation', desc: 'Web Crypto generates 32 cryptographically secure random bytes via crypto.getRandomValues()' },
                  { step: '2', title: 'Prefix + Encoding', desc: 'Encoded as base64url with prefix lfk_v1_ — readable and versionable' },
                  { step: '3', title: 'SHA-256 Hashing', desc: 'Raw key is hashed with SHA-256. Only the hash + fingerprint (first 8 hex chars) are stored in D1' },
                  { step: '4', title: 'Request Authentication', desc: 'Each request sends the raw key as Bearer token. Worker hashes it and compares against D1' },
                  { step: '5', title: 'Permission Check', desc: 'JSON-encoded permission array checked before routing to provider. Supports scoped access control' },
                ].map(s => (
                  <div key={s.step} className="flex gap-3 p-3 rounded-md bg-muted/40 border border-border">
                    <span className="text-lava font-mono text-sm font-bold flex-shrink-0 w-5">{s.step}</span>
                    <div>
                      <p className="text-xs font-semibold text-foreground">{s.title}</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <CodeBlock lang="typescript — key format" code={`// Format: lfk_v1_{base64url(32_random_bytes)}
// Example:
'lfk_v1_A1b2C3d4E5f6G7h8I9j0K1l2M3n4O5p6Q7r8S9t0'

// Stored in D1 (never the raw key):
{
  key_hash:    "sha256_hex_64_chars...",
  fingerprint: "a1b2c3d4",  // first 8 chars of hash
}

// Authorization header:
Authorization: Bearer lfk_v1_YOUR_KEY`} />
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Permissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  { id: 'ai:stream', desc: 'Stream AI completions through gateway' },
                  { id: 'ai:embed', desc: 'Generate vector embeddings' },
                  { id: 'db:read', desc: 'Read from D1 EdgeSQL database' },
                  { id: 'db:write', desc: 'Write to D1 EdgeSQL database' },
                  { id: 'storage:read', desc: 'Read from R2 storage bucket' },
                  { id: 'storage:write', desc: 'Write to R2 storage bucket' },
                  { id: 'kv:read', desc: 'Read from KV namespace' },
                  { id: 'kv:write', desc: 'Write to KV namespace' },
                  { id: 'admin', desc: 'Full platform admin access' },
                ].map(p => (
                  <div key={p.id} className="flex items-start gap-2 p-2 rounded bg-muted/30 border border-border">
                    <code className="text-[10px] font-mono text-lava font-semibold flex-shrink-0 mt-0.5">{p.id}</code>
                    <span className="text-[11px] text-muted-foreground">{p.desc}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Gateway */}
        <TabsContent value="gateway" className="mt-4 space-y-4">
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <BrainCircuit className="w-4 h-4 text-lava" />
                Supported Providers
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {[
                  { name: 'Cloudflare Workers AI', endpoint: 'gateway.ai.cloudflare.com/v1/{account}/lavaflow-gateway/workers-ai', note: 'Default · zero-config' },
                  { name: 'OpenAI', endpoint: 'api.openai.com/v1', note: 'GPT-4o, GPT-4o-mini' },
                  { name: 'Anthropic', endpoint: 'api.anthropic.com/v1', note: 'Claude Opus 4.5, Sonnet' },
                  { name: 'Vercel AI Gateway', endpoint: 'api.vercel.ai/v1', note: 'Multi-provider routing' },
                  { name: 'Ollama (Local)', endpoint: 'localhost:11434/v1', note: 'Self-hosted, OpenAI-compat' },
                  { name: 'Custom', endpoint: 'your-endpoint.com/v1', note: 'Any OpenAI-compatible API' },
                ].map(p => (
                  <div key={p.name} className="flex items-start justify-between gap-3 p-2.5 rounded-md bg-muted/30 border border-border">
                    <div>
                      <p className="text-xs font-semibold text-foreground">{p.name}</p>
                      <code className="text-[10px] font-mono text-muted-foreground">{p.endpoint}</code>
                    </div>
                    <Badge variant="outline" className="text-[9px] h-4 px-1.5 border-border text-muted-foreground flex-shrink-0">
                      {p.note}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Cloudflare AI Gateway headers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-muted-foreground">
                Include these headers when routing via CF AI Gateway for full observability and rate limiting.
              </p>
              <CodeBlock lang="http headers" code={`Authorization: Bearer lfk_v1_YOUR_KEY
cf-aig-authorization: Bearer CF_AIG_TOKEN
X-LavaFlow-Key-Id: key_id_for_tracing
Content-Type: application/json`} />
            </CardContent>
          </Card>
        </TabsContent>

        {/* Reference */}
        <TabsContent value="reference" className="mt-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">AI Gateway (OpenAI-compatible)</p>
            <div className="space-y-1.5">
              <Endpoint method="POST" path="/api/gateway/v1/chat/completions" description="Chat completion (streaming + sync)">
                <CodeBlock lang="request body" code={`{
  "model": "@cf/meta/llama-3.1-8b-instruct",
  "messages": [
    { "role": "system", "content": "You are helpful." },
    { "role": "user", "content": "Hello!" }
  ],
  "stream": true,
  "temperature": 0.7,
  "max_tokens": 1024
}`} />
              </Endpoint>
              <Endpoint method="POST" path="/api/gateway/v1/completions" description="Text completion">
                <CodeBlock lang="request body" code={`{ "model": "gpt-4o-mini", "prompt": "Complete this:", "max_tokens": 256 }`} />
              </Endpoint>
              <Endpoint method="POST" path="/api/gateway/v1/embeddings" description="Text embeddings">
                <CodeBlock lang="request body" code={`{ "model": "@cf/baai/bge-base-en-v1.5", "input": "Hello world" }`} />
              </Endpoint>
              <Endpoint method="GET" path="/api/gateway/v1/models" description="List available models" />
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">API Keys</p>
            <div className="space-y-1.5">
              <Endpoint method="GET" path="/api/keys" description="List all API keys for authenticated user" />
              <Endpoint method="POST" path="/api/keys" description="Create a new API key">
                <CodeBlock lang="request body" code={`{
  "name": "Production Gateway",
  "permissions": ["ai:stream", "db:read"],
  "expiresInDays": 90  // optional, null = never
}`} />
              </Endpoint>
              <Endpoint method="PATCH" path="/api/keys/:id" description="Enable/disable or update a key">
                <CodeBlock lang="request body" code={`{ "is_active": false }`} />
              </Endpoint>
              <Endpoint method="DELETE" path="/api/keys/:id" description="Permanently revoke a key" />
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">AI Providers</p>
            <div className="space-y-1.5">
              <Endpoint method="GET" path="/api/providers" description="List configured providers" />
              <Endpoint method="POST" path="/api/providers" description="Add a new provider">
                <CodeBlock lang="request body" code={`{
  "name": "My OpenAI",
  "provider_type": "openai",
  "endpoint": "https://api.openai.com/v1",
  "model_list": ["gpt-4o", "gpt-4o-mini"],
  "priority": 0
}`} />
              </Endpoint>
              <Endpoint method="PATCH" path="/api/providers/:id" description="Update provider settings" />
              <Endpoint method="DELETE" path="/api/providers/:id" description="Remove a provider" />
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Analytics</p>
            <div className="space-y-1.5">
              <Endpoint method="GET" path="/api/analytics?range=7d" description="Usage metrics (1d, 7d, 30d, 90d)" />
            </div>
          </div>

          <Separator />

          <div>
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Database</p>
            <div className="space-y-1.5">
              <Endpoint method="GET" path="/api/database/info" description="Table schema and row counts" />
              <Endpoint method="POST" path="/api/database/query" description="Execute SQL query (admin only)">
                <CodeBlock lang="request body" code={`{ "sql": "SELECT * FROM users LIMIT 10", "params": [] }`} />
              </Endpoint>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  )
}
